import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import GeoChart from "../../components/common/charts/GeoChart";
import PageHeader from "../../components/common/pageHeader/PageHeader";
import SectionHeading from "../../components/common/SectionHeading";
import Wrapper from "../../components/utils/Wrapper";
import { Select, Table, Spin, message } from "antd";
import { getArticlesByCountry } from "../../apis/contentApis/getArticles";
import getFormatNumber from "../../utils/helperFunctions/getFormatedNumber";
import getGraphOverviewData from "../../apis/contentApis/getGraphOverviewData";
import sanitizeHtml from "../../utils/UI/sanitizeHtml";
import sizeCalculator from "../../utils/helperFunctions/sizeLabelCompiler";
import { NewsCardProps } from "../../components/common/ArticleCards";

// Initial static data as fallback
const fallbackData = [
  ["Country", "Published"],
  ["Egypt", 287],
  ["Saudi Arabia", 287],
  ["United Arab Emirates (UAE)", 287],
  ["Lebanon", 287],
  ["Morocco", 287],
  ["Kuwait", 287],
  ["Libya", 287],
  ["Palestine", 287],
  ["Jordan", 287],
  ["Bahrain", 287],
  ["Oman", 287],
  ["Syria", 287],
  ["Iraq", 287],
  ["Qatar", 287],
  ["Algeria", 287],
  ["Yemen", 287],
  ["Tunisia", 287],
];

const options = {
  colorAxis: {
    colors: ["#CBDAF2", "#97B4E6", "#658BCB", "#2F69CC"],
  },
};

// Interface for country data
interface CountryData {
  country: string;
  count: number;
}

export default function Demographics() {
  const navigate = useNavigate();
  const countryArray = JSON.parse(localStorage.getItem("user_data") || "[]");
  const countryOptions = countryArray?.Country?.map((country: any) => ({
    value: country,
    label: country,
  })) || [];
  
  const [selectedCountry, setSelectedCountry] = useState(
    countryOptions.length > 0 ? countryOptions[0].label : ""
  );
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mapLoading, setMapLoading] = useState(true);
  const [geoData, setGeoData] = useState<any[][]>(fallbackData);
  const [pagination, setPagination] = useState({
    total: 0,
    pageSize: 10,
    current: 1,
  });

  // Navigate to article insight with article data
  const navigateToArticleInsight = (record: any) => {
    const description = sanitizeHtml(record.description || "");
    const mediaType = record.mediaType || "digital";
    const size = sizeCalculator(mediaType, description, record.length || 0);
    
    const sentimentValue = record.sentiment?.includes("ONLY")
      ? "Positive"
      : record.sentiment || "Negative";
    
    const articleData: NewsCardProps = {
      publisher: record.sourceName,
      title: record.title,
      img: record.image_url,
      date: record.pubDate,
      sentiment: sentimentValue,
      traffic: record.traffic || 0,
      description: description,
      sourceIcon: record.source_icon,
      ave: record.ave,
      keywords: record.keywords,
      country: [selectedCountry], // Using selected country since we're filtering by it
      language: record.language,
      mediaType: mediaType,
      size: size,
      link: record.link
    };

    navigate("/dashboard/content/article-insight", { state: articleData });
  };

  // Table columns with updated configuration
  const tableColumns = [
    {
      title: "Publication",
      dataIndex: "sourceName",
      key: "sourceName",
      render: (text: string) => text || "N/A",
    },
    {
      title: "Article",
      dataIndex: "title",
      key: "title",
      render: (text: string) => text || "N/A",
    },
    {
      title: "Estimated Traffic",
      dataIndex: "traffic",
      key: "traffic",
      render: (text: string) => getFormatNumber(Number(text)) || "N/A",
    },
    {
      title: "AVE",
      dataIndex: "ave",
      key: "ave",
      render: (text: string) =>
        text ? `$${getFormatNumber(Number(text))}` : "N/A",
    },
    {
      title: "Sentiment",
      dataIndex: "sentiment",
      key: "sentiment",
      render: (text: string) => text?.toLowerCase() || "N/A",
    },
  ];

  // Function to transform country data into GeoChart format
  const transformData = (dataArray: CountryData[] | undefined) => {
    const transformedData: any[][] = [["Country", "Published"]];

    if (dataArray && dataArray.length > 0) {
      dataArray.forEach((item) => {
        transformedData.push([item.country, item.count]);
      });
    } else {
      return fallbackData; // Use fallback data if no data available
    }

    return transformedData;
  };

  // Fetch country data for the GeoChart
  const fetchCountryData = useCallback(async () => {
    setMapLoading(true);
    try {
      const currentDate = new Date();
      const startDate = new Date(currentDate);
      startDate.setDate(currentDate.getDate() - 30); // Last 30 days
      const endDate = new Date(currentDate);
      
      const digitalFilter = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        crawlerType: "alltable",
        dateType: "date",
      };

      const response = await getGraphOverviewData(digitalFilter);
      
      if (response?.status && response?.tableData?.length > 0) {
        const countryCounts = response.tableData[0]?.countryCounts;
        if (countryCounts && countryCounts.length > 0) {
          const formattedGeoData = transformData(countryCounts);
          setGeoData(formattedGeoData);
        }
      } else {
        console.warn("No country data available, using fallback data");
        setGeoData(fallbackData);
      }
    } catch (error) {
      console.error("Error fetching country data:", error);
      message.error("Failed to fetch country data");
      setGeoData(fallbackData);
    } finally {
      setMapLoading(false);
    }
  }, []);

  const fetchArticlesByCountry = async (country: string) => {
    if (!country) return;
    
    setLoading(true);
    try {
      const response = await getArticlesByCountry(country);
      if (response.success) {
        const articlesWithKeys = response.articles.map((article: any, index: number) => ({
          ...article,
          key: `article-${index}`,
          mediaType: article.mediaType || "digital", // Ensure mediaType is set
        }));
        setArticles(articlesWithKeys);
        setPagination(prev => ({
          ...prev,
          total: articlesWithKeys.length,
        }));
      } else {
        message.error(response.message || "Failed to fetch articles");
        setArticles([]);
      }
    } catch (error) {
      console.error("Error fetching articles:", error);
      message.error("An error occurred while fetching articles");
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountryData();
  }, [fetchCountryData]);

  useEffect(() => {
    if (selectedCountry) {
      fetchArticlesByCountry(selectedCountry);
    }
  }, [selectedCountry]);

  const handleTableChange = (pagination: any) => {
    setPagination(prev => ({
      ...prev,
      current: pagination.current,
    }));
  };

  return (
    <>
      {/* Heading section */}
      <PageHeader heading="PR Geography" showDateRangeFilter={false} />
      <div className="w-full p-[3px] flex flex-col md:gap-4 gap-0">
        {/* Map section */}
        <Wrapper insights={false}>
          <SectionHeading content="PR Geography" toolTipText="A global heatmap visualizing media coverage distribution by region, highlighting key areas of press activity." />
          {mapLoading ? (
            <div className="flex justify-center items-center h-[306px]">
              <Spin size="large" />
            </div>
          ) : (
            <GeoChart data={geoData} options={options} showProgressBars={false} />
          )}
        </Wrapper>
        
        {/* Table section */}
        <Wrapper insights={false}>
          <div className="flex items-center mb-4">
            <span className="text-base font-semibold text-hardGrey">Filter by Country: </span> &nbsp;
            <Select
              value={selectedCountry}
              style={{ width: 170 }}
              options={countryOptions}
              onChange={(value) => setSelectedCountry(value)}
            />
          </div>
          <div className="w-full overflow-x-auto">
            {loading ? (
              <div className="flex justify-center items-center p-10">
                <Spin size="large" />
              </div>
            ) : (
              <Table 
                columns={tableColumns} 
                dataSource={articles}
                onRow={(record) => ({
                  onClick: () => navigateToArticleInsight(record),
                  style: { cursor: 'pointer' }
                })}
                pagination={{
                  total: pagination.total,
                  pageSize: pagination.pageSize,
                  current: pagination.current,
                  showSizeChanger: false,
                  showQuickJumper: false,
                  showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total}`,
                }}
                onChange={handleTableChange}
                className="w-full"
                size="middle"
                locale={{ emptyText: "No articles found for this country" }}
              />
            )}
          </div>
        </Wrapper>
      </div>
    </>
  );
}
